import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    acquireRfqLock,
    releaseRfqLock,
    setupLockReleaseOnUnload,
    scheduleReleaseLock,
    cancelPendingRelease,
    LockInfo
} from '../utils/rfqLock';

interface UseRfqLockReturn {
    lockInfo: LockInfo | null;
    showLockModal: boolean;
    setShowLockModal: (show: boolean) => void;
    releaseLock: () => Promise<void>;
}

export const useRfqLock = (requestId: string | null): UseRfqLockReturn => {
    const { token } = useAuth();
    const [lockInfo, setLockInfo] = useState<LockInfo | null>(null);
    const [showLockModal, setShowLockModal] = useState(false);

    useEffect(() => {
        if (!requestId || !token) return;

        let cleanupUnload: (() => void) | null = null;
        let mounted = true;

        const acquireLock = async () => {
            // Cancel any pending release from previous page navigation
            const wasCancelled = cancelPendingRelease();

            try {
                const result = await acquireRfqLock(requestId, token);

                if (mounted) {
                    if (result.success) {
                        if (!wasCancelled) {
                            console.log('✅ RFQ lock acquired for request:', requestId);
                        } else {
                            console.log('🔄 RFQ lock re-verified (navigation)');
                        }

                        // Setup beforeunload handler for tab close
                        cleanupUnload = setupLockReleaseOnUnload(requestId, token);
                    } else if (result.lock_info) {
                        // Locked by another user
                        console.warn('⚠️ RFQ is locked by another user');
                        setLockInfo(result.lock_info);
                        setShowLockModal(true);
                    }
                }
            } catch (error) {
                console.error('❌ Error acquiring RFQ lock:', error);
            }
        };

        acquireLock();

        return () => {
            mounted = false;

            if (cleanupUnload) {
                cleanupUnload();
            }

            // Instead of releasing immediately, schedule it.
            // If the user is navigating to another tab in the same RFQ,
            // the next component will cancel this schedule.
            scheduleReleaseLock(requestId, token);
        };
    }, [requestId, token]);

    return {
        lockInfo,
        showLockModal,
        setShowLockModal,
        releaseLock: async () => {
            if (requestId && token) {
                await releaseRfqLock(requestId, token);
            }
        }
    };
};
